"use client";

import { CSSProperties, JSX, useId } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  Legend,
  LegendIndex,
  LegendItem,
  LegendLabel,
  LegendStatus,
  LegendValue,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import { polarToCartesian } from "@/lib/svg-geometry";
import type { ModuleCycleData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import {
  cycleDemoData,
  phasesToLegendItems,
  type CyclePhase,
} from "./shared/cycles";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";

const tabular: CSSProperties = { fontVariantNumeric: "tabular-nums" };

export default function ModuleNodeCycle({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleCycleData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const phases = (data?.phases as CyclePhase[] | undefined) ?? cycleDemoData;

  const { hovered, selected, effective, setHovered, select, setLabelRef } =
    useLegendHoverSelect();
  const hoverGesture = useHoverGesture();

  const size = 320;
  const { svgRef, height: dynamicH } = useSvgContentHeight(size, [phases]);
  const cx = size / 2;
  const cy = size / 2;
  const trackR = 105;
  const nodeR = 22;
  const labelOffset = 32;
  const arrowMarkerId = useId();

  const activePhase =
    effective !== null
      ? phases[effective]
      : phases.find((p) => p.status === "active") ?? phases[0];
  const doneCount = phases.filter((p) => p.status === "done").length;
  const legendItems = phasesToLegendItems(phases);

  return (
    <div
      className={cn("flex w-full justify-center", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex flex-col items-center gap-4 w-full max-w-[560px]">
        <div className="relative shrink-0">
          <svg
            ref={svgRef}
            width={size}
            height={dynamicH}
            viewBox={`0 0 ${size} ${dynamicH}`}
            style={{ overflow: "visible" }}
          >
            <defs>
              <marker
                id={arrowMarkerId}
                markerWidth="7"
                markerHeight="7"
                refX="5"
                refY="3.5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M 0 0 L 6 3.5 L 0 7 Z" fill="var(--status-neutral)" />
              </marker>
            </defs>

            <circle
              cx={cx}
              cy={cy}
              r={trackR}
              fill="none"
              stroke="var(--grid-strong)"
              strokeWidth="1"
              strokeDasharray="3 4"
            />

            {phases.map((_, i) => {
              const nextIdx = (i + 1) % phases.length;
              const currAngle = (i / phases.length) * 360;
              const nextAngle = (nextIdx / phases.length) * 360;
              const startAngle = currAngle + 14;
              const endAngle = (nextIdx === 0 ? 360 : nextAngle) - 14;
              const startPos = polarToCartesian(cx, cy, trackR, startAngle);
              const endPos = polarToCartesian(cx, cy, trackR, endAngle);
              return (
                <path
                  key={`arc-${i}`}
                  d={`M ${startPos.x} ${startPos.y} A ${trackR} ${trackR} 0 0 1 ${endPos.x} ${endPos.y}`}
                  fill="none"
                  stroke="var(--status-neutral)"
                  strokeWidth="1"
                  strokeOpacity="0.7"
                  markerEnd={`url(#${arrowMarkerId})`}
                />
              );
            })}

            {phases.map((phase, i) => {
              const angle = (i / phases.length) * 360;
              const pos = polarToCartesian(cx, cy, trackR, angle);
              const labelPos = polarToCartesian(cx, cy, trackR + labelOffset, angle);
              const isDone = phase.status === "done";
              const isActive = phase.status === "active";
              const isPending = phase.status === "pending";
              const isHovered = effective === i;
              const dim = effective !== null && !isHovered;

              return (
                <g
                  key={phase.id}
                  onPointerEnter={(e) => {
                    if (hoverGesture.enter(e)) setHovered(i);
                  }}
                  onPointerLeave={(e) => {
                    if (hoverGesture.leave(e)) setHovered(null);
                  }}
                  onClick={() => select(i, { kind: "node" })}
                  style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                  opacity={dim ? 0.4 : 1}
                >
                  {isActive && (
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={nodeR + 5}
                      fill="none"
                      stroke="var(--status-1)"
                      strokeWidth="1"
                      opacity="0.4"
                    />
                  )}
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={nodeR}
                    fill={isActive ? "var(--status-1)" : "var(--card)"}
                    stroke={
                      isActive
                        ? "var(--status-1)"
                        : isDone
                          ? "var(--status-2)"
                          : "var(--status-neutral)"
                    }
                    strokeWidth="1.5"
                    strokeDasharray={isPending ? "3 3" : "0"}
                  />
                  {isDone ? (
                    <path
                      d={`M ${pos.x - 5} ${pos.y - 0.5} L ${pos.x - 1} ${
                        pos.y + 4
                      } L ${pos.x + 6} ${pos.y - 4}`}
                      fill="none"
                      stroke="var(--status-1)"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <text
                      x={pos.x}
                      y={pos.y + 4}
                      textAnchor="middle"
                      fontSize="12"
                      fontWeight={isActive ? 600 : 500}
                      fill={isActive ? "var(--bg)" : "var(--muted)"}
                      style={tabular}
                    >
                      {i + 1}
                    </text>
                  )}
                  <foreignObject
                    x={labelPos.x - 44}
                    y={labelPos.y - 14}
                    width={88}
                    height={32}
                    overflow="visible"
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: isActive || isHovered ? "var(--fg)" : "var(--muted)",
                        fontSize: "11px",
                        fontWeight: isActive || isHovered ? 600 : 400,
                        lineHeight: 1.15,
                        textAlign: "center",
                        overflowWrap: "anywhere",
                        transition: "color 0.15s",
                      }}
                    >
                      {stripInlineMarkdown(phase.label)}
                    </div>
                  </foreignObject>
                </g>
              );
            })}

            <text x={cx} y={cy - 10} textAnchor="middle" fontSize="11" fill="var(--subtle)">
              {t("Currently")}
            </text>
            <foreignObject x={cx - 60} y={cy - 4} width={120} height={24} overflow="visible">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--fg)",
                  fontSize: "17px",
                  fontWeight: 600,
                  lineHeight: 1.15,
                  textAlign: "center",
                  overflowWrap: "anywhere",
                }}
              >
                {stripInlineMarkdown(activePhase.label)}
              </div>
            </foreignObject>
            <text x={cx} y={cy + 28} textAnchor="middle" fontSize="11" fill="var(--muted)" style={tabular}>
              {doneCount} / {phases.length} done
            </text>
          </svg>
        </div>

        <SwipeScroll className="w-full pt-6 border-t border-dashed border-[var(--border-dashed)]">
          <Legend
            items={legendItems}
            hoveredIndex={hovered}
            onHoverChange={setHovered}
            selectedIndex={selected}
            onSelect={select}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 flex-col items-start gap-1 px-3 py-2">
              <div className="flex items-baseline gap-2">
                <LegendIndex />
                <LegendLabel className="text-sm" />
              </div>
              <div className="flex items-baseline gap-2">
                <LegendValue className="text-xs text-legend-muted-foreground tabular-nums" />
                <LegendStatus />
              </div>
            </LegendItem>
          </Legend>
        </SwipeScroll>
      </div>
    </div>
  );
}
